import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  SelectChangeEvent,
  Container,
  Snackbar,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { createUser } from '../../services/userService';

// Estilos personalizados para el contenedor del formulario
const FormContainer = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: '24px',
  marginTop: '16px',
});

// Estilos para las secciones del formulario
const FormSection = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

// Estilos para los botones de acción
const ActionButtons = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '16px',
  marginTop: '24px',
});

type UserProfile = 'root' | 'admin' | 'mkt' | 'usuario';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  profile: UserProfile;
}

const AddUserPage: React.FC = () => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    profile: 'usuario',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'El nombre es requerido';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'El apellido es requerido';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'El email no es válido';
    }
    
    if (!formData.profile) {
      newErrors.profile = 'El perfil es requerido';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Limpiar el error cuando el usuario comienza a escribir
    if (errors[name as keyof FormData]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  const handleSelectChange = (e: SelectChangeEvent<UserProfile>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    if (errors.profile) {
      setErrors({
        ...errors,
        profile: '',
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const userData = {
        email: formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName,
        profile: formData.profile,
      };
      
      const response = await createUser(userData);
      
      setSnackbar({
        open: true,
        message: 'Usuario creado exitosamente',
        severity: 'success',
      });
      
      // Redirigir a la lista de usuarios después de un breve retraso
      setTimeout(() => {
        navigate('/users');
      }, 1500);
      
    } catch (error) {
      console.error('Error al crear el usuario:', error);
      const errorMessage = error instanceof Error ? error.message : 'Ocurrió un error inesperado al crear el usuario';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const profileOptions = [
    { value: 'root', label: 'Root' },
    { value: 'admin', label: 'Administrador' },
    { value: 'mkt', label: 'Marketing' },
    { value: 'usuario', label: 'Usuario' },
  ];


  return (
    <Container maxWidth={false} disableGutters sx={{ maxWidth: '100%', px: 0 }}>
      <Box sx={{ mb: 4, width: '100%', p: { xs: 1, sm: 2 } }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Volver
        </Button>
        
        <Typography variant="h4" component="h1" gutterBottom>
          Agregar Nuevo Usuario
        </Typography>
        
        <Paper sx={{ p: { xs: 2, sm: 3 }, width: '100%' }}>
          <form onSubmit={handleSubmit}>
            <FormContainer>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                <Box sx={{ display: 'flex', gap: 2, width: '100%', '& .MuiTextField-root': { flex: 1 } }}>
                  <TextField
                    label="Nombre"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                    margin="normal"
                    variant="outlined"
                  />
                  
                  <TextField
                    label="Apellido"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    error={!!errors.lastName}
                    helperText={errors.lastName}
                    margin="normal"
                    variant="outlined"
                  />
                </Box>
                
                <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                  <TextField
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={!!errors.email}
                    helperText={errors.email}
                    margin="normal"
                    variant="outlined"
                    sx={{ flex: 2 }}
                  />
                  
                  <FormControl margin="normal" error={!!errors.profile} sx={{ flex: 1 }}>
                    <InputLabel id="profile-label">Perfil</InputLabel>
                    <Select
                      labelId="profile-label"
                      name="profile"
                      value={formData.profile}
                      onChange={handleSelectChange}
                      label="Perfil"
                    >
                      {profileOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.profile && (
                      <FormHelperText>{errors.profile}</FormHelperText>
                    )}
                  </FormControl>
                </Box>
              </Box>
            </FormContainer>
            
            <ActionButtons>
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                size="large"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                size="large"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Guardando...' : 'Guardar Usuario'}
              </Button>
            </ActionButtons>
          </form>
        </Paper>
      </Box>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddUserPage;
